import { scaleTime } from 'd3-scale';
import { timeDay } from 'd3-time';

import './styles/graphic.scss';
import data from './data/trades.json';
// The "data" tab of the copytext Google Sheet (one row per company, with a
// "post-text" column) is published to data.json in production.
import postSheet from './data/data.json';
import trumpIcon from './images/trump-icon.png';

const POST_ICON = 14; // diameter of the circular post marker

// palette (from design handoff)
const BUY = '#e2662e';
const SELL = '#14766c';
const POST = '#cdc9c2';
const GRID = '#efede9';
const RAIL = '#e3e0db';

const ROW_H = 106;
// Rail sits low in the strip: buys (many, large) get the tall upper zone,
// sells (one small trade) get a compact lower zone.
const RAIL_Y = 66;

const parseDate = (s) => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};

// AP style: spell out March–July, abbreviate the rest
const AP_MONTHS = ['Jan.', 'Feb.', 'March', 'April', 'May', 'June', 'July', 'Aug.', 'Sept.', 'Oct.', 'Nov.', 'Dec.'];

const fmtMonthDay = dt => `${AP_MONTHS[dt.getMonth()].toUpperCase()} ${dt.getDate()}`;

const POST_HEADER = `<div class="ttl-post-head"><img class="ttl-tw" src="${trumpIcon}" alt=""><div class="ttl-post-name"><span class="ttl-post-author">Donald J. Trump</span><span class="ttl-post-handle">@realDonaldTrump</span></div></div>`;

const escapeHtml = s => String(s)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;');

// Render a disclosed range (e.g. "$100K–$250K") as a centered, two-line label.
// topY is the baseline of the first line; the second line drops below it.
function amountLabel(x, topY, range, anchor) {
  const parts = String(range || '').split('\u2013');
  const lo = escapeHtml((parts[0] || '').trim());
  const hi = escapeHtml((parts[1] || '').trim());
  // smaller bound on top, larger below
  const line1 = hi ? `${lo}\u2013` : lo;
  const l2 = hi ? `<tspan x="${x}" y="${topY + 12}">${hi}</tspan>` : '';
  return `<text class="ttl-amt" text-anchor="${anchor || 'middle'}">`
    + `<tspan x="${x}" y="${topY}">${line1}</tspan>${l2}</text>`;
}

// Given label x-positions (left to right), return a text-anchor for each.
// Labels are centered by default; when two would collide, the earlier one is
// right-aligned and the later one left-aligned so they splay around their arrows.
function labelAnchors(xs) {
  const HALFW = 22; // approx half a centered label's width (incl. halo)
  const FULLW = 44; // approx full width of an edge-aligned label
  const anchors = xs.map(() => 'middle');
  let prevRight = -Infinity;
  let prevIdx = -1;
  xs.forEach((xn, i) => {
    if (xn - HALFW < prevRight) {
      anchors[i] = 'start';
      if (prevIdx >= 0 && anchors[prevIdx] === 'middle') anchors[prevIdx] = 'end';
      prevRight = xn + FULLW;
    } else {
      prevRight = xn + HALFW;
    }
    prevIdx = i;
  });
  return anchors;
}

const MONTH_ABBR = {
  jan: 1,
  feb: 2,
  mar: 3,
  apr: 4,
  may: 5,
  jun: 6,
  jul: 7,
  aug: 8,
  sep: 9,
  oct: 10,
  nov: 11,
  dec: 12,
};

// Normalize a sheet date like "Aug 04, 2025" (or an ISO string) to YYYY-MM-DD.
function toIso(s) {
  const str = String(s || '').trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.slice(0, 10);
  const m = str.match(/^([A-Za-z]{3,})\.?\s+(\d{1,2}),?\s+(\d{4})$/);
  if (!m) return '';
  const mon = MONTH_ABBR[m[1].slice(0, 3).toLowerCase()];
  if (!mon) return '';
  return `${m[3]}-${String(mon).padStart(2, '0')}-${String(+m[2]).padStart(2, '0')}`;
}

// Look up the post for a card from the sheet's "data" tab, matching by the post
// date (falling back to a company column if one is present). Returns the post
// text and its display date.
function postFor(stock) {
  const rows = Array.isArray(postSheet) ? postSheet : [];
  const iso = toIso(stock.center);
  const key = String(stock.name).trim().toLowerCase();
  const row = rows.find((r) => {
    const dateMatch = iso && toIso(r['post-date'] || r.postDate || r.date) === iso;
    const co = r.company || r.Company || r.name || r.Name || r.stock || '';
    const coMatch = String(co).trim().toLowerCase() === key;
    return dateMatch || coMatch;
  });
  if (!row) return { text: '', date: '', url: '' };
  const text = row['post-text'] || row.postText || row.post_text || row.text || '';
  const date = row['post-date'] || row.postDate || row.date || '';
  const url = row['post-url'] || row.postUrl || row.post_url || row.url || '';
  return { text: String(text).trim(), date: String(date).trim(), url: String(url).trim() };
}

// Build the inner SVG markup for a single company's 2-week window strip.
function rowSvg(stock, geo) {
  const {
    scale, stripW, center, minMid, maxMid, postTop, sectionLabel,
  } = geo;
  const railY = RAIL_Y;
  const domain = scale.domain();
  const lo = +domain[0];
  const hi = +domain[1];
  const inView = t => t >= lo && t <= hi;

  // Arrow stem length encodes trade value on a shared log scale (in-view
  // trades span orders of magnitude, from a few thousand to hundreds of
  // thousands), so small vs large trades read clearly and lengths stay
  // comparable company-to-company.
  const MIN_LEN = 6;
  const MAX_LEN = 40;
  const lnLo = Math.log(minMid || 1);
  const lnSpan = Math.log(maxMid || 1) - lnLo;
  const stemLen = (mid) => {
    if (!mid || lnSpan <= 0) return MIN_LEN;
    const t = Math.max(0, Math.min(1, (Math.log(mid) - lnLo) / lnSpan));
    return MIN_LEN + (MAX_LEN - MIN_LEN) * t;
  };

  // 1. light daily gridlines across the window
  const days = timeDay.range(domain[0], timeDay.offset(domain[1], 1));
  let svg = days
    .map((d, i) => {
      const x = scale(d).toFixed(1);
      const edge = i === 0 || i === days.length - 1;
      // the first and last gridlines (window edges) are a touch darker and
      // a bit shorter than the interior daily gridlines
      const stroke = edge ? '#cdc9c2' : GRID;
      const y1 = edge ? railY - 8 : 0;
      const y2 = edge ? railY + 8 : ROW_H;
      return `<line x1="${x}" x2="${x}" y1="${y1}" y2="${y2}" stroke="${stroke}" stroke-width="1"></line>`;
    })
    .join('');

  // 2. emphasized line on the centered post day
  const xC = scale(center).toFixed(1);
  svg += `<line x1="${xC}" x2="${xC}" y1="0" y2="${ROW_H}" stroke="${RAIL}" stroke-width="1"></line>`;

  // 3. center hairline
  svg += `<line x1="0" x2="${stripW}" y1="${railY}" y2="${railY}" stroke="${RAIL}" stroke-width="1"></line>`;

  // 4. posts — circular Trump icon lifted above the strip, tethered to the rail
  const r = POST_ICON / 2;
  // icon center lands on the callout's bottom edge (postTop px above the
  // strip), so the circle sits half over the white post-callout background
  const postCy = postTop != null ? postTop : -18;
  svg += (stock.posts || [])
    .filter(p => inView(+parseDate(p)))
    .map((p) => {
      const x = scale(parseDate(p)).toFixed(1);
      const line = `<line x1="${x}" x2="${x}" y1="${postCy}" y2="${railY}" stroke="${POST}" stroke-width="2"></line>`;
      // only the center post's icon renders here; other in-view post icons go
      // in a back layer (see backPostsSvg) so they tuck behind the callout
      const circle = +parseDate(p) === +center
        ? `<circle cx="${x}" cy="${postCy}" r="${r}" fill="url(#ttl-trump)" stroke="#fff" stroke-width="1"></circle>`
        : '';
      return line + circle;
    })
    .join('');

  // 4b. optional caption along the connector, between the callout and the
  // trade arrows (used on the American Eagle card, which has room to spare)
  if (sectionLabel) {
    svg += `<text class="ttl-section" x="0" y="-13" text-anchor="start">${escapeHtml(sectionLabel)}</text>`;
  }

  // 5. buys (lollipop above rail) + amount label
  const buys = (stock.buys || [])
    .filter(b => inView(+parseDate(b.date)))
    .sort((a, b) => parseDate(a.date) - parseDate(b.date))
    .map(b => ({ b, xn: scale(parseDate(b.date)) }));
  const buyAnchors = labelAnchors(buys.map(o => o.xn));
  svg += buys
    .map((o, i) => {
      const x = o.xn.toFixed(1);
      const tipY = (railY - stemLen(o.b.mid)).toFixed(1);
      const topY = +tipY - 24;
      // nudge Nvidia's first two annotations apart so they read cleanly
      let labelX = o.xn;
      if (stock.name === 'Nvidia' && i === 0) labelX += 3;
      else if (stock.name === 'Nvidia' && i === 1) labelX -= 3;
      return `${`<line x1="${x}" x2="${x}" y1="${railY}" y2="${tipY}" stroke="${BUY}" stroke-width="1.5"></line>`
        + `<path d="M0,-7 L5.6,0 L-5.6,0 Z" fill="${BUY}" stroke="#fff" stroke-width="1" transform="translate(${x},${tipY})"></path>`}${
        amountLabel(labelX.toFixed(1), topY, o.b.range, buyAnchors[i])}`;
    })
    .join('');

  // 6. sells (lollipop below rail) + amount label
  const sells = (stock.sells || [])
    .filter(s => inView(+parseDate(s.date)))
    .sort((a, b) => parseDate(a.date) - parseDate(b.date))
    .map(s => ({ s, xn: scale(parseDate(s.date)) }));
  const sellAnchors = labelAnchors(sells.map(o => o.xn));
  svg += sells
    .map((o, i) => {
      const x = o.xn.toFixed(1);
      const tipY = (railY + stemLen(o.s.mid)).toFixed(1);
      const topY = +tipY + 17;
      return `${`<line x1="${x}" x2="${x}" y1="${railY}" y2="${tipY}" stroke="${SELL}" stroke-width="1.5"></line>`
        + `<path d="M0,7 L5.6,0 L-5.6,0 Z" fill="${SELL}" stroke="#fff" stroke-width="1" transform="translate(${x},${tipY})"></path>`}${
        amountLabel(x, topY, o.s.range, sellAnchors[i])}`;
    })
    .join('');

  return `<svg width="${stripW}" height="${ROW_H}" viewBox="0 0 ${stripW} ${ROW_H}" style="display:block; overflow:visible;">${svg}</svg>`;
}

// Back-layer SVG holding the icons for in-view posts that AREN'T the center
// post, so they render behind the white callout box.
function backPostsSvg(stock, geo) {
  const {
    scale, stripW, center, postTop,
  } = geo;
  const domain = scale.domain();
  const lo = +domain[0];
  const hi = +domain[1];
  const r = POST_ICON / 2;
  const postCy = postTop != null ? postTop : -18;
  const markers = (stock.posts || [])
    .filter(p => +parseDate(p) >= lo && +parseDate(p) <= hi && +parseDate(p) !== +center)
    .map((p) => {
      const x = scale(parseDate(p)).toFixed(1);
      return `<circle cx="${x}" cy="${postCy}" r="${r}" fill="url(#ttl-trump)" stroke="#fff" stroke-width="1"></circle>`;
    })
    .join('');
  if (!markers) return '';
  return `<svg width="${stripW}" height="${ROW_H}" viewBox="0 0 ${stripW} ${ROW_H}" style="display:block; overflow:visible;">${markers}</svg>`;
}

export default function (config, container, isMobile) {
  const { initialWidth } = config;

  // guard against a malformed/placeholder data file
  if (!data || !Array.isArray(data.stocks) || !data.stocks.length) {
    container.html('<p style="font-family:\'CNN Sans Display\',\'CNN\',Helvetica,Arial,sans-serif; font-size:14px; color:#777; padding:24px;">Timeline data is unavailable. Run <code>python3 scripts/process_data.py</code> to build <code>src/data/trades.json</code>.</p>');
    if (window.pymChild) window.pymChild.sendHeight();
    return;
  }

  const stocks = (data.stocks || []).filter(s => s.center);
  const windowDays = data.windowDays || 7;

  // Largest in-view trade (midpoint) across every card. Shared so arrow
  // lengths are comparable from one company to the next.
  const winOf = (s) => {
    const c = parseDate(s.center);
    return [
      s.windowStart ? +parseDate(s.windowStart) : +timeDay.offset(c, -windowDays),
      s.windowEnd ? +parseDate(s.windowEnd) : +timeDay.offset(c, windowDays),
    ];
  };
  let maxMid = 0;
  let minMid = Infinity;
  stocks.forEach((s) => {
    const [ws, we] = winOf(s);
    [...(s.buys || []), ...(s.sells || [])].forEach((t) => {
      const d = +parseDate(t.date);
      if (d >= ws && d <= we && t.mid) {
        if (t.mid > maxMid) maxMid = t.mid;
        if (t.mid < minMid) minMid = t.mid;
      }
    });
  });
  if (!Number.isFinite(minMid)) minMid = 0;

  // Each company is a card with its own independent axis spanning
  // [center-7, center+7]. Cards are 3-across on desktop, stacked on mobile.
  const maxInner = 1050 - 36 * 2;
  const contentW = Math.min(initialWidth, maxInner);
  const cols = isMobile ? 1 : 3;
  const colGap = 16;
  const rowGap = isMobile ? 8 : 20;
  const cardPadX = 14;
  const cardOuter = (contentW - colGap * (cols - 1)) / cols;
  const stripW = Math.max(80, Math.floor(cardOuter - cardPadX * 2));

  // --- legend swatches ---------------------------------------------------
  const legend = `
    <div class="ttl-legend">
      <span class="ttl-legend-item"><img class="ttl-legend-icon" src="${trumpIcon}" width="14" height="14" alt="">Pro\u2013company Truth Social post</span>
      <span class="ttl-legend-item"><svg width="14" height="14" viewBox="0 0 14 14"><path d="M7,2 L12,11 L2,11 Z" fill="${BUY}"></path></svg>Bought</span>
      <span class="ttl-legend-item"><svg width="14" height="14" viewBox="0 0 14 14"><path d="M7,12 L12,3 L2,3 Z" fill="${SELL}"></path></svg>Sold</span>
    </div>`;

  // --- one block per company ---------------------------------------------
  const blocks = stocks
    .map((s) => {
      const center = parseDate(s.center);
      const winStart = s.windowStart ? parseDate(s.windowStart) : timeDay.offset(center, -windowDays);
      const winEnd = s.windowEnd ? parseDate(s.windowEnd) : timeDay.offset(center, windowDays);
      const scale = scaleTime().domain([winStart, winEnd]).range([0, stripW]);

      const ticks = [
        { d: winStart, align: 'start' },
        { d: winEnd, align: 'end' },
      ];
      let prevMonth = null;
      const axisLabels = ticks
        .map((t, i) => {
          const x = scale(t.d).toFixed(1);
          // spell out the month on the first label, and again whenever the
          // window crosses into a new month; otherwise just the day number
          const showMonth = t.d.getMonth() !== prevMonth;
          prevMonth = t.d.getMonth();
          let text = showMonth ? fmtMonthDay(t.d) : `${t.d.getDate()}`;
          if (i === 0) text += `<span class="ttl-yr">${t.d.getFullYear()}</span>`;
          // nudge the end labels outward a touch so they sit flush with the
          // window-edge tick lines
          const transform = t.align === 'start'
            ? 'translateX(-3px)'
            : t.align === 'end' ? 'translateX(calc(-100% + 3px))' : 'translateX(-50%)';
          return `<div class="ttl-month" style="left:${x}px; transform:${transform};">${text}</div>`;
        })
        .join('');

      // Label the connector gap with a "Stock trades" caption. The gap/icon
      // offset is uniform across all cards so every white callout box comes
      // out the same size.
      const geo = {
        scale,
        stripW,
        center,
        minMid,
        maxMid,
        postTop: -34,
        sectionLabel: 'Stock trades',
      };

      // Callout sits above the centered post icon (which always lands at the
      // strip midpoint, since the window is symmetric around the post date).
      const post = postFor(s);
      const dateLine = post.date
        ? `<div class="ttl-post-date">${escapeHtml(post.date)}</div>`
        : '';
      const calloutInner = `${POST_HEADER}<div class="ttl-post-text">${escapeHtml(post.text)}</div>${dateLine}`;
      const callout = post.text
        ? (post.url
          ? `<a class="ttl-callout ttl-callout--link" href="${escapeHtml(post.url)}" target="_blank" rel="noopener">${calloutInner}</a>`
          : `<div class="ttl-callout">${calloutInner}</div>`)
        : '';

      return `
        <div class="ttl-cell">
          <div class="ttl-card" style="padding-left:${cardPadX}px; padding-right:${cardPadX}px;">
            <div class="ttl-name">${s.name}</div>
            <div class="ttl-plot" style="width:${stripW}px;">
              ${backPostsSvg(s, geo) ? `<div class="ttl-back">${backPostsSvg(s, geo)}</div>` : ''}
              ${callout}
              <div class="ttl-strip">${rowSvg(s, geo)}</div>
            </div>
            <div class="ttl-axis" style="width:${stripW}px;">${axisLabels}</div>
          </div>
        </div>`;
    })
    .join('');

  const gridStyle = `grid-template-columns:repeat(${cols}, 1fr); gap:${rowGap}px ${colGap}px; width:${contentW}px;`;

  const html = `
    <div class="ttl-chart ${isMobile ? 'ttl-chart--mobile' : ''}">
      <svg width="0" height="0" style="position:absolute" aria-hidden="true"><defs><pattern id="ttl-trump" patternContentUnits="objectBoundingBox" width="1" height="1"><image href="${trumpIcon}" xlink:href="${trumpIcon}" width="1" height="1" preserveAspectRatio="xMidYMid slice"></image></pattern></defs></svg>
      <div class="container ttl-legend-wrap">${legend}</div>
      <div class="ttl-multiples" style="${gridStyle}">${blocks}</div>
    </div>`;

  container.html(html);

  if (window.pymChild) {
    window.pymChild.sendHeight();
  }
}
